import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, Eye, RefreshCw, FileText, Users, ChevronDown, ChevronUp } from 'lucide-react';

export default function ReferralTextsEditor() {
  const [texts, setTexts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editing, setEditing] = useState(false); // Bearbeitungsmodus
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    beforeSpending: true,
    mainSection: true,
    wichtig: true,
    howItWorks: true,
    packages: true
  });
  
  // Form state (16 Felder)
  const [headerTitle, setHeaderTitle] = useState('');
  const [headerSubtitle, setHeaderSubtitle] = useState('');
  const [beforeSpendingTitle, setBeforeSpendingTitle] = useState('');
  const [beforeSpendingText, setBeforeSpendingText] = useState('');
  const [mainTitle, setMainTitle] = useState('');
  const [mainSubtitle, setMainSubtitle] = useState('');
  const [wichtigText, setWichtigText] = useState('');
  const [howItWorksTitle, setHowItWorksTitle] = useState('');
  const [step1Title, setStep1Title] = useState('');
  const [step1Text, setStep1Text] = useState('');
  const [step2Title, setStep2Title] = useState('');
  const [step2Text, setStep2Text] = useState('');
  const [step3Title, setStep3Title] = useState('');
  const [step3Text, setStep3Text] = useState('');
  const [packagesTitle, setPackagesTitle] = useState('');
  const [packagesSubtitle, setPackagesSubtitle] = useState('');
  
  useEffect(() => {
    loadTexts();
  }, []);
  
  const loadTexts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/referral/texts/admin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Texte');
      }
      
      const data = await response.json();
      
      setTexts(data);
      setHeaderTitle(data.header_title || '');
      setHeaderSubtitle(data.header_subtitle || '');
      setBeforeSpendingTitle(data.before_spending_title || '');
      setBeforeSpendingText(data.before_spending_text || '');
      setMainTitle(data.main_title || '');
      setMainSubtitle(data.main_subtitle || '');
      setWichtigText(data.wichtig_text || '');
      setHowItWorksTitle(data.how_it_works_title || '');
      setStep1Title(data.step1_title || '');
      setStep1Text(data.step1_text || '');
      setStep2Title(data.step2_title || '');
      setStep2Text(data.step2_text || '');
      setStep3Title(data.step3_title || '');
      setStep3Text(data.step3_text || '');
      setPackagesTitle(data.packages_title || '');
      setPackagesSubtitle(data.packages_subtitle || '');
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading texts:', err);
      setError('Fehler beim Laden der Texte');
      setLoading(false);
    }
  };
  
  const saveTexts = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const token = localStorage.getItem('snapshot_token');
      
      const payload = {
        header_title: headerTitle,
        header_subtitle: headerSubtitle,
        before_spending_title: beforeSpendingTitle,
        before_spending_text: beforeSpendingText,
        main_title: mainTitle,
        main_subtitle: mainSubtitle,
        wichtig_text: wichtigText,
        how_it_works_title: howItWorksTitle,
        step1_title: step1Title,
        step1_text: step1Text,
        step2_title: step2Title,
        step2_text: step2Text,
        step3_title: step3Title,
        step3_text: step3Text,
        packages_title: packagesTitle,
        packages_subtitle: packagesSubtitle
      };
      
      console.log('💾 Speichere Referral Texte:', payload);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/referral/texts/admin`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API Error:', errorData);
        throw new Error(errorData.detail || 'Update fehlgeschlagen');
      }
      
      const result = await response.json();
      console.log('✅ API Response:', result);
      
      await loadTexts(); // Reload to confirm
      setSuccess('✅ Alle Texte erfolgreich gespeichert!');
      setSaving(false);
      setEditing(false); // Bearbeitungsmodus beenden
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error saving texts:', err);
      setError('Fehler beim Speichern: ' + err.message);
      setSaving(false);
    }
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  const cancelEditing = () => {
    // Reload data to reset changes
    loadTexts();
    setEditing(false);
    setError(null);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-black">
        <div className="flex items-center gap-3 text-white">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Lade Texte...</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-8 h-8 text-emerald-400" />
            <h1 className="text-3xl font-bold">Referral-Seite Texte</h1>
          </div>
          <p className="text-white/60">
            Bearbeite ALLE Texte auf der Freunde-Einladen-Seite
          </p>
        </div>
        
        {/* Success/Error Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-semibold">Fehler</p>
              <p className="text-red-300/80 text-sm">{error}</p>
            </div>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-start gap-3">
            <Save className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-emerald-400 font-semibold">Erfolg</p>
              <p className="text-emerald-300/80 text-sm">{success}</p>
            </div>
          </div>
        )}
        
        {/* Split Screen Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* LEFT: Editor */}
          <div className="space-y-4">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-emerald-400" />
                <h2 className="text-xl font-bold">Texte bearbeiten</h2>
              </div>
              
              {/* Action Buttons */}
              {!editing ? (
                <button
                  onClick={() => setEditing(true)}
                  className="w-full mb-6 py-3 px-6 rounded-xl font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 hover:shadow-xl hover:shadow-emerald-500/30 transition-all flex items-center justify-center gap-2"
                >
                  <FileText className="w-5 h-5" />
                  Texte bearbeiten
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <button
                    onClick={saveTexts}
                    disabled={saving}
                    className={`
                      py-3 px-6 rounded-xl font-bold flex items-center justify-center gap-2
                      transition-all duration-200
                      ${saving 
                        ? 'bg-white/10 text-white/40 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 hover:shadow-xl hover:shadow-emerald-500/30'
                      }
                    `}
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Speichern...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Speichern
                      </>
                    )}
                  </button>
                  <button
                    onClick={cancelEditing}
                    disabled={saving}
                    className="py-3 px-6 rounded-xl font-bold bg-white/10 text-white hover:bg-white/20 transition-all flex items-center justify-center gap-2"
                  >
                    Abbrechen
                  </button>
                </div>
              )}
              
              <div className="space-y-4">
                
                {/* Section 1: Header */}
                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('header')}
                    className="w-full px-4 py-3 bg-white/5 flex items-center justify-between hover:bg-white/10 transition-colors"
                  >
                    <span className="font-semibold">1️⃣ Header Section</span>
                    {expandedSections.header ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSections.header && (
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">Header Title</label>
                        <input
                          value={headerTitle}
                          onChange={(e) => setHeaderTitle(e.target.value)}
                          disabled={!editing}
                          className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="SNAPs"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">Header Subtitle</label>
                        <input
                          value={headerSubtitle}
                          onChange={(e) => setHeaderSubtitle(e.target.value)}
                          disabled={!editing}
                          className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Verwalte deine SNAPs..."
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Section 2: Before Spending */}
                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('beforeSpending')}
                    className="w-full px-4 py-3 bg-white/5 flex items-center justify-between hover:bg-white/10 transition-colors"
                  >
                    <span className="font-semibold">2️⃣ "Bevor du Geld ausgibst" Box</span>
                    {expandedSections.beforeSpending ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSections.beforeSpending && (
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">Title (mit Emoji)</label>
                        <input
                          value={beforeSpendingTitle}
                          onChange={(e) => setBeforeSpendingTitle(e.target.value)}
                          disabled={!editing}
                          className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Bevor du Geld ausgibst... 💡"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">Text</label>
                        <textarea
                          value={beforeSpendingText}
                          onChange={(e) => setBeforeSpendingText(e.target.value)}
                          disabled={!editing}
                          className="w-full h-24 px-4 py-3 bg-black/50 border border-white/20 rounded-xl text-white resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Schnapp dir kostenlose SNAPs..."
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Section 3: Main Section */}
                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('mainSection')}
                    className="w-full px-4 py-3 bg-white/5 flex items-center justify-between hover:bg-white/10 transition-colors"
                  >
                    <span className="font-semibold">3️⃣ Main Section (Header)</span>
                    {expandedSections.mainSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSections.mainSection && (
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">Main Title</label>
                        <input
                          value={mainTitle}
                          onChange={(e) => setMainTitle(e.target.value)}
                          disabled={!editing}
                          className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Freunde einladen & SNAPs verdienen"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">Main Subtitle</label>
                        <textarea
                          value={mainSubtitle}
                          onChange={(e) => setMainSubtitle(e.target.value)}
                          disabled={!editing}
                          className="w-full h-20 px-4 py-3 bg-black/50 border border-white/20 rounded-xl text-white resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Du erhältst SNAPs einmalig..."
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Section 4: Wichtig Box */}
                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('wichtig')}
                    className="w-full px-4 py-3 bg-white/5 flex items-center justify-between hover:bg-white/10 transition-colors"
                  >
                    <span className="font-semibold">4️⃣ Wichtig-Box (Gelb)</span>
                    {expandedSections.wichtig ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSections.wichtig && (
                    <div className="p-4">
                      <label className="block text-sm font-semibold text-white/80 mb-2">Wichtig-Text</label>
                      <textarea
                        value={wichtigText}
                        onChange={(e) => setWichtigText(e.target.value)}
                        disabled={!editing}
                        className="w-full h-32 px-4 py-3 bg-black/50 border border-white/20 rounded-xl text-white resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Wichtig: Belohnungen werden..."
                      />
                    </div>
                  )}
                </div>
                
                {/* Section 5: How It Works */}
                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('howItWorks')}
                    className="w-full px-4 py-3 bg-white/5 flex items-center justify-between hover:bg-white/10 transition-colors"
                  >
                    <span className="font-semibold">5️⃣ "So funktioniert's" (3 Steps)</span>
                    {expandedSections.howItWorks ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSections.howItWorks && (
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">Section Title</label>
                        <input
                          value={howItWorksTitle}
                          onChange={(e) => setHowItWorksTitle(e.target.value)}
                          disabled={!editing}
                          className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="So funktioniert's"
                        />
                      </div>
                      
                      {/* Step 1 */}
                      <div className="pl-4 border-l-2 border-emerald-500/30 space-y-2">
                        <p className="text-xs text-emerald-400 font-semibold">STEP 1</p>
                        <input
                          value={step1Title}
                          onChange={(e) => setStep1Title(e.target.value)}
                          disabled={!editing}
                          className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Link teilen"
                        />
                        <textarea
                          value={step1Text}
                          onChange={(e) => setStep1Text(e.target.value)}
                          disabled={!editing}
                          className="w-full h-20 px-4 py-3 bg-black/50 border border-white/20 rounded-xl text-white resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Teile deinen persönlichen..."
                        />
                      </div>
                      
                      {/* Step 2 */}
                      <div className="pl-4 border-l-2 border-emerald-500/30 space-y-2">
                        <p className="text-xs text-emerald-400 font-semibold">STEP 2</p>
                        <input
                          value={step2Title}
                          onChange={(e) => setStep2Title(e.target.value)}
                          disabled={!editing}
                          className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Freund kauft ein Paket"
                        />
                        <textarea
                          value={step2Text}
                          onChange={(e) => setStep2Text(e.target.value)}
                          disabled={!editing}
                          className="w-full h-20 px-4 py-3 bg-black/50 border border-white/20 rounded-xl text-white resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Dein Freund erstellt..."
                        />
                      </div>
                      
                      {/* Step 3 */}
                      <div className="pl-4 border-l-2 border-emerald-500/30 space-y-2">
                        <p className="text-xs text-emerald-400 font-semibold">STEP 3</p>
                        <input
                          value={step3Title}
                          onChange={(e) => setStep3Title(e.target.value)}
                          disabled={!editing}
                          className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="SNAPs verdienen (einmalig!)"
                        />
                        <textarea
                          value={step3Text}
                          onChange={(e) => setStep3Text(e.target.value)}
                          disabled={!editing}
                          className="w-full h-20 px-4 py-3 bg-black/50 border border-white/20 rounded-xl text-white resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Du erhältst einmalig..."
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Section 6: Packages */}
                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('packages')}
                    className="w-full px-4 py-3 bg-white/5 flex items-center justify-between hover:bg-white/10 transition-colors"
                  >
                    <span className="font-semibold">6️⃣ Packages Section</span>
                    {expandedSections.packages ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSections.packages && (
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">Packages Title</label>
                        <input
                          value={packagesTitle}
                          onChange={(e) => setPackagesTitle(e.target.value)}
                          disabled={!editing}
                          className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="SNAP Pakete"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-2">Packages Subtitle</label>
                        <input
                          value={packagesSubtitle}
                          onChange={(e) => setPackagesSubtitle(e.target.value)}
                          disabled={!editing}
                          className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Nutze SNAPs für..."
                        />
                      </div>
                    </div>
                  )}
                </div>
                
              </div>
              
            </div>
          </div>
          
          {/* RIGHT: Live Preview */}
          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sticky top-6">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-emerald-400" />
                <h2 className="text-xl font-bold">Live-Vorschau</h2>
              </div>
              
              <div className="space-y-6">
                
                {/* Header Preview */}
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-emerald-400">{headerTitle || 'SNAPs'}</h3>
                  <p className="text-white/60 text-sm">{headerSubtitle || 'Verwalte deine SNAPs...'}</p>
                </div>
                
                {/* Before Spending Preview */}
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                  <p className="text-emerald-400 font-semibold mb-2">{beforeSpendingTitle || 'Bevor du Geld ausgibst... 💡'}</p>
                  <p className="text-emerald-300/80 text-sm whitespace-pre-wrap">{beforeSpendingText || 'Schnapp dir kostenlose SNAPs...'}</p>
                </div>
                
                {/* Main Section Preview */}
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-white">{mainTitle || 'Freunde einladen & SNAPs verdienen'}</h3>
                  <p className="text-white/60 text-sm whitespace-pre-wrap">{mainSubtitle || 'Du erhältst SNAPs einmalig...'}</p>
                </div>
                
                {/* Wichtig Preview */}
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                  <p className="text-yellow-400 text-sm leading-relaxed whitespace-pre-wrap">
                    {wichtigText || 'Wichtig: Belohnungen werden...'}
                  </p>
                </div>
                
                {/* How It Works Preview */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-white">{howItWorksTitle || 'So funktioniert\'s'}</h3>
                  
                  {/* Step 1 */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-emerald-400 font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-1">{step1Title || 'Link teilen'}</h4>
                      <p className="text-white/60 text-sm whitespace-pre-wrap">{step1Text || 'Teile deinen persönlichen...'}</p>
                    </div>
                  </div>
                  
                  {/* Step 2 */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-emerald-400 font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-1">{step2Title || 'Freund kauft ein Paket'}</h4>
                      <p className="text-white/60 text-sm whitespace-pre-wrap">{step2Text || 'Dein Freund erstellt...'}</p>
                    </div>
                  </div>
                  
                  {/* Step 3 */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-emerald-400 font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-1">{step3Title || 'SNAPs verdienen (einmalig!)'}</h4>
                      <p className="text-white/60 text-sm whitespace-pre-wrap">{step3Text || 'Du erhältst einmalig...'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Packages Preview */}
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <h3 className="text-xl font-bold text-white">{packagesTitle || 'SNAP Pakete'}</h3>
                  <p className="text-white/60 text-sm">{packagesSubtitle || 'Nutze SNAPs für...'}</p>
                </div>
                
                {/* Info */}
                <div className="mt-6 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                  <p className="text-emerald-400 text-xs">
                    💡 <span className="font-semibold">Tipp:</span> Die Vorschau aktualisiert sich automatisch beim Tippen
                  </p>
                </div>
                
              </div>
            </div>
          </div>
          
        </div>
        
      </div>
    </div>
  );
}
